/**
 * useFeatureFlag - Hook for checking feature flag status
 *
 * Fetches and caches feature flags from the backend.
 * Returns the enabled status for specific flags.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'

import { createLogger } from '../utils/logger'

const logger = createLogger('useFeatureFlag')

// Cache for feature flags (shared across hook instances)
let flagsCache = null
let flagsCacheTime = null
const CACHE_TTL = 60000 // 1 minute
// Promise lock to prevent concurrent fetches
let fetchPromise = null

/**
 * Shared fetch function that handles caching and prevents race conditions
 * @param {boolean} force - Force a refresh, ignoring cache
 * @returns {Promise<object>} - The fetched flags
 */
const fetchFlagsShared = async (force = false) => {
  const now = Date.now()

  // Check cache first (unless forced refresh)
  if (!force && flagsCache && flagsCacheTime && now - flagsCacheTime < CACHE_TTL) {
    return flagsCache
  }

  // If a fetch is already in progress, wait for it
  if (fetchPromise) {
    return fetchPromise
  }

  // Start a new fetch and store the promise
  fetchPromise = (async () => {
    try {
      const response = await fetch('/api/feature-flags/my-flags', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to fetch feature flags')
      }

      const data = await response.json()
      flagsCache = data.flags || {}
      flagsCacheTime = Date.now()

      return flagsCache
    } finally {
      // Clear the promise lock after fetch completes
      fetchPromise = null
    }
  })()

  return fetchPromise
}

/**
 * Hook to check if a specific feature flag is enabled
 * @param {string} flagKey - The feature flag key to check
 * @returns {{ enabled: boolean, loading: boolean, error: string | null }}
 */
export const useFeatureFlag = (flagKey) => {
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuthStore()

  useEffect(() => {
    const checkFlag = async () => {
      // If not logged in, flag is disabled
      if (!user) {
        setEnabled(false)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const flags = await fetchFlagsShared()
        setEnabled(flags[flagKey] || false)
        setError(null)
      } catch (err) {
        logger.error('Error checking feature flag:', err)
        setError(err.message)
        setEnabled(false)
      } finally {
        setLoading(false)
      }
    }

    checkFlag()
  }, [flagKey, user])

  return { enabled, loading, error }
}

/**
 * Hook to get all feature flags at once
 * @returns {{ flags: object, loading: boolean, error: string | null, refresh: () => void }}
 */
export const useFeatureFlags = () => {
  const [flags, setFlags] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuthStore()

  const fetchFlags = useCallback(
    async (force = false) => {
      // If not logged in, no flags
      if (!user) {
        setFlags({})
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const fetchedFlags = await fetchFlagsShared(force)
        setFlags(fetchedFlags)
        setError(null)
      } catch (err) {
        logger.error('Error fetching feature flags:', err)
        setError(err.message)
        setFlags({})
      } finally {
        setLoading(false)
      }
    },
    [user]
  )

  useEffect(() => {
    fetchFlags()
  }, [fetchFlags])

  const refresh = useCallback(() => {
    fetchFlags(true)
  }, [fetchFlags])

  return { flags, loading, error, refresh }
}

/**
 * Clear the feature flags cache
 * Call this when user logs out or session changes
 */
export const clearFeatureFlagsCache = () => {
  flagsCache = null
  flagsCacheTime = null
  fetchPromise = null
}

/**
 * Reset all state for testing purposes
 * @internal
 */
export const __resetForTesting = () => {
  flagsCache = null
  flagsCacheTime = null
  fetchPromise = null
}

export default useFeatureFlag
